import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EChart } from 'components/ui/EChart';
import { useHoursVsMinimum } from 'hooks/api/useDashboard';
import type { EChartsCoreOption } from 'echarts/core';

interface Props {
  managerId?: string;
  weekStartDate?: string;
  onDataClick?: (ctx: { employeeId: string; employeeName: string }) => void;
}

const HoursVsMinimumWidget: React.FC<Props> = ({ managerId, weekStartDate, onDataClick }) => {
  const { data, loading } = useHoursVsMinimum(managerId, weekStartDate);

  // Default view shows only employees who fell short of their minimum.
  // Toggle "Show All" to include those who met or exceeded the threshold.
  const [showAll, setShowAll] = useState(false);

  const allEmployees = data?.employees || [];
  const employees = useMemo(
    () =>
      showAll
        ? allEmployees
        : allEmployees.filter((e) => Number(e.loggedHours || 0) < Number(e.minimumHours || 0)),
    [allEmployees, showAll],
  );
  const hiddenCount = allEmployees.length - employees.length;

  const chartData = useMemo(() => {
    const names = employees.map((e) => (e.employeeName || '').trim() || '—');
    const minimum = employees.map((e) => Number(e.minimumHours || 0));
    const logged = employees.map((e, i) => ({
      value: Number(e.loggedHours || 0),
      itemStyle: {
        color: Number(e.loggedHours || 0) >= minimum[i] ? '#16a34a' : '#d97706',
      },
      employeeId: e.employeeId,
      employeeName: e.employeeName,
    }));
    return { names, minimum, logged };
  }, [employees]);

  const dynamicHeight = Math.max(260, employees.length * 32 + 40);

  const option: EChartsCoreOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: any) => `${Number(v).toFixed(2)} h`,
      },
      legend: {
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
        data: ['Minimum', 'Logged'],
      },
      grid: { top: 10, bottom: 32, left: 120, right: 20, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { formatter: '{value}h' },
      },
      yAxis: {
        type: 'category',
        data: chartData.names,
        inverse: true,
        axisTick: { alignWithLabel: true },
      },
      series: [
        {
          name: 'Minimum',
          type: 'bar',
          data: chartData.minimum,
          itemStyle: { color: '#94a3b8' },
          barWidth: 10,
        },
        {
          name: 'Logged',
          type: 'bar',
          data: chartData.logged,
          barWidth: 10,
        },
      ],
    }),
    [chartData],
  );

  const events = useMemo(
    () => ({
      click: (params: any) => {
        if (onDataClick && params?.data?.employeeId) {
          onDataClick({
            employeeId: params.data.employeeId,
            employeeName: params.data.employeeName || '',
          });
        }
      },
    }),
    [onDataClick],
  );

  return (
    <div className="dashboard-widget" style={{ gridColumn: '1 / -1' }}>
      <div className="dashboard-widget-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconSystem.chart size={18} />
          <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>
            Hours Logged vs Minimum
          </h3>
          {!showAll && hiddenCount > 0 && (
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              ({hiddenCount} meeting target hidden)
            </span>
          )}
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            style={{ accentColor: 'var(--primary)' }}
          />
          Show all
        </label>
      </div>
      {!loading && employees.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          {allEmployees.length === 0
            ? 'No employees to display.'
            : 'All employees met their minimum for this week.'}
        </div>
      ) : (
        <EChart option={option} height={dynamicHeight} loading={loading} onEvents={events} />
      )}
    </div>
  );
};

export default HoursVsMinimumWidget;
